import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header, Footer } from "@/components/Layout";
import { Paywall } from "@/components/Paywall";
import { RatingStars } from "@/components/RatingStars";
import {
  WELLBEING_TEXT_BEFORE,
  HelplineLink,
  ScoreRing,
  FeedbackBody,
  LockedBody,
  RewardCoupon,
} from "@/components/results/ResultParts";
import {
  LoadingScreen,
  NotFoundScreen,
  ProcessingScreen,
  DeclinedScreen,
  FailedScreen,
} from "@/components/results/StatusScreens";
import { RecommendPrompt } from "@/components/results/RecommendPrompt";
import { Button } from "@/components/ui/button";
import { Download, RotateCcw, Lightbulb, Heart, Lock, Loader2 } from "lucide-react";
import { getSubmission, getPaymentStatus, bandMeta } from "@/lib/api";
import { buildPdf } from "@/lib/pdf";

export default function Results() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(!!sessionId);
  const [rewardCoupon, setRewardCoupon] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await getSubmission(id));
    } catch {
      setData(false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll for AI completion.
  useEffect(() => {
    if (data && data.status === "processing") {
      const t = setTimeout(load, 3000);
      return () => clearTimeout(t);
    }
  }, [data, load]);

  // Verify payment when returning from Stripe.
  const pollPayment = useCallback(
    async (attempt) => {
      if (attempt >= 10) {
        setVerifying(false);
        toast.error("We couldn't confirm payment yet. If you paid, refresh in a moment.");
        return;
      }
      try {
        const r = await getPaymentStatus(sessionId);
        if (r.payment_status === "paid" || r.unlocked) {
          if (r.reward_coupon) setRewardCoupon(r.reward_coupon);
          setVerifying(false);
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          await load();
          toast.success("Payment successful — full feedback unlocked!");
          return;
        }
        if (r.status === "expired") {
          setVerifying(false);
          toast.error("That payment session expired. Please try again.");
          return;
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error("Payment status poll failed:", e);
      }
      setTimeout(() => pollPayment(attempt + 1), 2000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, load]
  );

  useEffect(() => {
    if (sessionId) {
      setVerifying(true);
      pollPayment(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const downloadPdf = () => {
    try {
      buildPdf(data).save(`statement-feedback-${data.course || "ucas"}.pdf`);
    } catch {
      toast.error("Couldn't generate the PDF.");
    }
  };

  if (loading) return <LoadingScreen />;
  if (data === false) return <NotFoundScreen />;
  if (!data) return null;
  if (data.status === "processing") return <ProcessingScreen />;
  if (data.status === "declined") return <DeclinedScreen message={data.message} />;

  const hasFreeFeedback =
    !!data.overall_band &&
    Array.isArray(data.questions) &&
    data.questions[0]?.feedback &&
    ((data.questions[0].feedback.working || []).length ||
      (data.questions[0].feedback.improvements || []).length);

  if (data.status === "failed" || !hasFreeFeedback) return <FailedScreen />;

  const unlocked = !!data.unlocked;

  return (
    <div className="min-h-screen flex flex-col bg-sand">
      <Header />

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 sm:px-8 py-12 sm:py-16">
        {verifying && (
          <div
            data-testid="verifying-payment"
            className="mb-8 rounded-2xl border border-clay/30 bg-clay/[0.05] px-6 py-4 text-sm text-ink-soft flex items-center gap-3"
          >
            <Loader2 className="h-4 w-4 animate-spin text-clay" />
            Confirming your payment…
          </div>
        )}

        {data.wellbeing_flag === true && (
          <div
            data-testid="wellbeing-banner"
            className="mb-8 rounded-2xl border border-forest/20 bg-forest/[0.05] px-6 py-5 text-sm text-ink-soft leading-relaxed flex items-start gap-3"
          >
            <Heart className="h-4 w-4 text-forest shrink-0 mt-0.5" strokeWidth={2} />
            <p>
              {WELLBEING_TEXT_BEFORE}
              <HelplineLink />.
            </p>
          </div>
        )}

        {/* Overall */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white border border-border rounded-2xl p-8 shadow-[0_8px_32px_rgba(17,42,34,0.03)] flex flex-col sm:flex-row gap-8 items-center"
        >
          <ScoreRing band={data.overall_band} />
          <div className="flex-1 text-center sm:text-left">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
              Overall rating · {data.course}
            </div>
            <h1 className="mt-2 font-heading text-2xl sm:text-3xl tracking-tight text-ink leading-snug" data-testid="verdict">
              {data.verdict}
            </h1>
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
              {unlocked ? (
                <Button
                  onClick={downloadPdf}
                  data-testid="download-pdf-button"
                  variant="outline"
                  className="border-border text-ink hover:bg-sand-deep rounded-lg px-6 py-5"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download as PDF
                </Button>
              ) : (
                <Button
                  data-testid="download-pdf-locked"
                  variant="outline"
                  onClick={() =>
                    document.querySelector('[data-testid="paywall"]')?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="border-border text-ink-muted hover:bg-sand-deep rounded-lg px-6 py-5"
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Download as PDF
                </Button>
              )}
              <RatingStars jobId={data.id} />
            </div>
          </div>
        </motion.section>

        {/* 25% off reward — sits between the overall comment and Q1 feedback */}
        {rewardCoupon && (
          <div className="mt-8">
            <RewardCoupon code={rewardCoupon} />
          </div>
        )}

        {/* Per-question */}
        <div className="mt-8 space-y-6">
          {data.questions.map((q, i) => {
            const locked = !q.feedback && !unlocked && q.index >= 1;
            return (
              <motion.section
                key={q.index}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-white border border-border rounded-2xl p-8 shadow-[0_8px_32px_rgba(17,42,34,0.03)]"
                data-testid={`result-card-${i + 1}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                      Question {i + 1}
                    </div>
                    <h3 className="mt-1 font-heading text-xl text-ink leading-snug max-w-md">
                      {q.question}
                    </h3>
                  </div>
                  {q.feedback ? (
                    <div className="shrink-0 text-right" data-testid={`sub-score-${i + 1}`}>
                      <div className="font-heading text-lg sm:text-xl" style={{ color: bandMeta(q.feedback.band).color }}>
                        {q.feedback.band}
                      </div>
                    </div>
                  ) : locked ? (
                    <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-clay/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-clay" data-testid={`locked-badge-${i + 1}`}>
                      <Lock className="h-3 w-3" strokeWidth={2.5} /> Locked
                    </div>
                  ) : null}
                </div>
                <div className="mt-6">
                  {q.feedback ? <FeedbackBody fb={q.feedback} /> : locked ? <LockedBody /> : null}
                </div>
              </motion.section>
            );
          })}
        </div>

        {/* Paywall (locked) or thinking questions (unlocked) */}
        {!unlocked ? (
          <div className="mt-8">
            <Paywall jobId={data.id} onUnlocked={load} />
          </div>
        ) : (
          Array.isArray(data.thinking_questions) &&
          data.thinking_questions.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15 }}
              className="mt-8 rounded-2xl border border-forest/20 bg-forest/[0.05] p-8"
              data-testid="thinking-questions"
            >
              <div className="flex items-center gap-2 text-forest">
                <Lightbulb className="h-5 w-5" strokeWidth={2} />
                <h2 className="font-heading text-xl text-ink">Questions to push your thinking</h2>
              </div>
              <p className="mt-2 text-ink-soft leading-relaxed max-w-2xl">
                Answer these honestly and you'll find stronger material for your statement.
              </p>
              <ol className="mt-5 space-y-3">
                {data.thinking_questions.map((q, i) => (
                  <li key={`tq-${i}`} className="flex gap-3 text-ink-soft leading-relaxed">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-forest/15 text-forest text-sm font-semibold">
                      {i + 1}
                    </span>
                    <span className="pt-0.5">{q}</span>
                  </li>
                ))}
              </ol>
            </motion.section>
          )
        )}

        {/* Recommend prompt — only after payment unlock, once per submission */}
        {unlocked && <RecommendPrompt jobId={data.id} />}

        {/* Wellbeing signpost — always shown on every result */}
        <div className="mt-12 pt-8 border-t border-border" data-testid="wellbeing-footnote">          <p className="text-xs text-ink-faint leading-relaxed max-w-2xl">
            {WELLBEING_TEXT_BEFORE}
            <HelplineLink />.
          </p>
        </div>

        <div className="mt-10 flex justify-center">
          <Button
            asChild
            variant="ghost"
            data-testid="analyse-again-button"
            className="text-ink-muted hover:text-ink rounded-lg px-6 py-5"
          >
            <Link to="/analyse">
              <RotateCcw className="mr-2 h-4 w-4" />
              Analyse another statement
            </Link>
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
